import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';
import { Paper, Group, Button, Text, Box } from '@mantine/core';

function ComplexityImpactChart({ processes }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [chartSize, setChartSize] = useState('medium');

  useEffect(() => {
    if (!canvasRef.current || !processes || processes.length === 0) {
      return;
    }

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Prepare data
    const complexityScores = [];
    const weightedScores = [];
    const processIds = [];

    processes.forEach(proc => {
      const complexityScore = parseFloat(proc.complexity_score || 3);
      const weightedScore = parseFloat(proc.weighted_score || 0);
      complexityScores.push(complexityScore);
      weightedScores.push(weightedScore);
      processIds.push(proc.id || '');
    });

    // Calculate averages for reference lines
    const avgComplexity = complexityScores.length > 0
      ? complexityScores.reduce((a, b) => a + b, 0) / complexityScores.length
      : 3;
    const avgScore = weightedScores.length > 0
      ? weightedScores.reduce((a, b) => a + b, 0) / weightedScores.length
      : 1.5;

    const ctx = canvasRef.current.getContext('2d');

    // Create chart
    chartRef.current = new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [{
          label: 'Processes',
          data: complexityScores.map((x, i) => ({ x, y: weightedScores[i] })),
          backgroundColor: 'rgba(121, 80, 242, 0.6)', // Purple (Mantine primary)
          borderColor: 'rgba(0, 0, 0, 1)',
          borderWidth: 1,
          pointRadius: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.5,
        plugins: {
          title: {
            display: true,
            text: 'Prioritization Matrix: Complexity vs Impact',
            font: { size: 18, weight: 'bold' }
          },
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function(context) {
                const index = context.dataIndex;
                return `${processIds[index]}: (Complexity: ${context.parsed.x}, Impact: ${context.parsed.y.toFixed(2)})`;
              }
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Complexity', font: { size: 14, weight: 'bold' } },
            min: 0.5,
            max: 5.5,
            ticks: {
              stepSize: 1,
              callback: (value) => {
                const labels = {
                  1: 'Very Low',
                  2: 'Low',
                  3: 'Medium',
                  4: 'High',
                  5: 'Custom'
                };
                return labels[value] || value;
              }
            }
          },
          y: {
            title: { display: true, text: 'Impact to Contact Center', font: { size: 14, weight: 'bold' } },
            min: 0
          }
        }
      },
      plugins: [{
        id: 'labels',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx;
          chart.data.datasets[0].data.forEach((point, index) => {
            const meta = chart.getDatasetMeta(0).data[index];
            ctx.fillStyle = 'black';
            ctx.font = 'bold 11px Arial';
            ctx.fillText(processIds[index], meta.x + 8, meta.y - 8);
          });
        }
      }]
    });

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [processes, chartSize]);

  if (!processes || processes.length === 0) {
    return (
      <Paper shadow="sm" p="xl" withBorder>
        <Text c="dimmed" ta="center">
          No processes available to display chart
        </Text>
      </Paper>
    );
  }

  const sizeOptions = {
    small: '400px',
    medium: '700px',
    large: '1000px'
  };

  return (
    <Paper shadow="sm" p="xl" withBorder>
      <Group justify="center" mb="md">
        <Text size="sm" fw={500} c="dimmed">
          Chart Size:
        </Text>
        <Button
          size="xs"
          variant={chartSize === 'small' ? 'filled' : 'default'}
          onClick={() => setChartSize('small')}
        >
          Small
        </Button>
        <Button
          size="xs"
          variant={chartSize === 'medium' ? 'filled' : 'default'}
          onClick={() => setChartSize('medium')}
        >
          Medium
        </Button>
        <Button
          size="xs"
          variant={chartSize === 'large' ? 'filled' : 'default'}
          onClick={() => setChartSize('large')}
        >
          Large
        </Button>
      </Group>
      <Box style={{ maxWidth: sizeOptions[chartSize], margin: '0 auto' }}>
        <canvas ref={canvasRef}></canvas>
      </Box>
    </Paper>
  );
}

export default ComplexityImpactChart;
